class AirDriedFrame extends HTMLElement {
  constructor() {
    super();
    console.log('✓ AirDriedFrame initialized');
    this.product = null;
    this.selectedOptions = {};
    this.selectedVariant = null;
    this.eventDate = null;
    this.init();
  }

  init() {
    // Parse product data from the JSON script tags
    this.parseProductData();

    // Set up mobile accordion behavior
    this.setupMobileAccordion();

    // Set up variant option selectors
    this.setupVariantOptions();

    // Set up date picker
    this.setupDatePicker();

    // Set up add to cart
    this.setupAddToCart();

    // Show first step
    this.showStep(1);

    // Initial variant selection
    this.updateSelectedVariant();
  }

  parseProductData() {
    // Parse main product data
    const productDataScript = document.getElementById('air-dried-frame-product-data');
    if (productDataScript) {
      try {
        this.product = JSON.parse(productDataScript.textContent);
        console.log('✓ Parsed product:', this.product.title);
        console.log('✓ Variants:', this.product.variants?.length || 0);
        console.log('✓ Options:', this.product.options || []);
      } catch (e) {
        console.error('✗ Failed to parse product data:', e);
      }
    } else {
      console.error('✗ No JSON script tag found with id="air-dried-frame-product-data"');
    }
  }

  setupMobileAccordion() {
    console.log('→ Setting up accordion');

    const steps = this.querySelectorAll('.frame-step');

    steps.forEach((step) => {
      const title = step.querySelector('.step-title');
      if (!title) return;

      title.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleAccordion(step);
      });
    });

    // Open first step by default
    const firstStep = this.querySelector('.frame-step');
    if (firstStep) {
      firstStep.classList.add('is-open');
    }

    console.log('✓ Accordion set up complete');
  }

  toggleAccordion(step) {
    const isOpen = step.classList.contains('is-open');

    // Close all other steps
    this.querySelectorAll('.frame-step.is-open').forEach(openStep => {
      if (openStep !== step) {
        openStep.classList.remove('is-open');
      }
    });

    // Toggle current step
    if (isOpen) {
      step.classList.remove('is-open');
    } else {
      step.classList.add('is-open');
    }
  }

  markStepCompleted(stepNumber) {
    const step = this.querySelector(`[js-step="${stepNumber}"]`);
    if (step) {
      step.classList.add('is-completed');
    }
  }

  openNextStep(currentStepNumber) {
    this.markStepCompleted(currentStepNumber);

    const nextStep = this.querySelector(`[js-step="${currentStepNumber + 1}"]`);
    if (nextStep) {
      const currentStep = this.querySelector(`[js-step="${currentStepNumber}"]`);
      if (currentStep) {
        currentStep.classList.remove('is-open');
      }

      setTimeout(() => {
        nextStep.classList.add('is-open');
      }, 200);
    }
  }

  setupVariantOptions() {
    console.log('→ Setting up variant options');

    const optionSelectors = this.querySelectorAll('[data-option-index]');

    optionSelectors.forEach((selector) => {
      const optionIndex = parseInt(selector.dataset.optionIndex);

      const radios = selector.querySelectorAll('input[type="radio"]');

      radios.forEach(radio => {
        // Initialize selectedOptions with first checked values
        if (radio.checked) {
          this.selectedOptions[`option${optionIndex + 1}`] = radio.value;
        }

        // Add change listener
        radio.addEventListener('change', () => {
          if (radio.checked) {
            this.handleOptionChange(optionIndex, radio.value);
          }
        });
      });
    });

    // Update availability
    this.updateOptionAvailability();

    // Find initial variant
    this.updateSelectedVariant();

    console.log('✓ Variant options set up complete');
  }

  handleOptionChange(optionIndex, value) {
    this.selectedOptions[`option${optionIndex + 1}`] = value;

    // Update availability of dependent options
    this.updateOptionAvailability();

    // Auto-select first available option in next steps if current selection is unavailable
    for (let i = optionIndex + 1; i < this.product.options.length; i++) {
      this.autoSelectFirstAvailableOption(i);
    }

    // Find matching variant
    this.updateSelectedVariant();

    // Move to next step
    this.showStep(optionIndex + 2);

    // Update summary
    this.updateSummary();
  }

  updateSelectedVariant() {
    if (!this.product || !this.product.variants) {
      console.error('✗ No product or variants available');
      return;
    }

    // Find variant that matches all selected options
    const matchingVariant = this.product.variants.find(variant => {
      return Object.keys(this.selectedOptions).every(key => {
        const optionValue = this.selectedOptions[key];
        return variant[key] === optionValue;
      });
    });

    if (matchingVariant) {
      this.selectedVariant = matchingVariant;
      console.log('✓ Selected variant:', matchingVariant.title, matchingVariant.id);

      // Update preview image
      this.updatePreviewImage(matchingVariant);

      // Update pricing
      this.updateSummary();
    } else {
      this.selectedVariant = null;
      console.warn('⚠ No matching variant found');
    }
  }

  updateOptionAvailability() {
    const optionSelectors = this.querySelectorAll('[data-option-index]');

    optionSelectors.forEach((selector) => {
      const optionIndex = parseInt(selector.dataset.optionIndex);

      // Skip availability checking for first option
      if (optionIndex === 0) {
        return;
      }

      const radios = selector.querySelectorAll('input[type="radio"]');

      radios.forEach(radio => {
        const testOptions = {};

        // Add all options before the current one
        for (let i = 1; i <= optionIndex; i++) {
          if (this.selectedOptions[`option${i}`]) {
            testOptions[`option${i}`] = this.selectedOptions[`option${i}`];
          }
        }

        // Add the current option being tested
        testOptions[`option${optionIndex + 1}`] = radio.value;

        // Check if any variant exists with these options
        const matchingVariants = this.product.variants.filter(variant => {
          return Object.keys(testOptions).every(key => {
            return variant[key] === testOptions[key];
          });
        });

        const variantExists = matchingVariants.length > 0;
        const hasAvailableVariant = matchingVariants.some(v => v.available);

        const label = radio.closest('.option-item');
        if (label) {
          if (!variantExists) {
            label.style.display = 'none';
            radio.disabled = true;
          } else if (!hasAvailableVariant) {
            label.style.display = '';
            label.classList.add('unavailable');
            radio.disabled = true;
          } else {
            label.style.display = '';
            label.classList.remove('unavailable');
            radio.disabled = false;
          }
        }
      });
    });
  }

  autoSelectFirstAvailableOption(optionIndex) {
    const selector = this.querySelector(`[data-option-index="${optionIndex}"]`);
    if (!selector) return;

    const radios = selector.querySelectorAll('input[type="radio"]');

    const currentlySelected = selector.querySelector('input[type="radio"]:checked');
    const currentLabel = currentlySelected?.closest('.option-item');
    const isCurrentHidden = currentLabel?.style.display === 'none';
    const isCurrentUnavailable = currentLabel?.classList.contains('unavailable');
    const isCurrentAvailable = !isCurrentHidden && !isCurrentUnavailable;

    if (currentlySelected && !isCurrentAvailable) {
      for (const radio of radios) {
        const label = radio.closest('.option-item');
        const isHidden = label?.style.display === 'none';
        const isUnavailable = label?.classList.contains('unavailable');
        if (!isHidden && !isUnavailable) {
          radio.checked = true;
          this.selectedOptions[`option${optionIndex + 1}`] = radio.value;
          return;
        }
      }
    }
  }

  updatePreviewImage(variant) {
    const imageElement = this.querySelector('[js-product-image]');
    const imageContainer = this.querySelector('[js-image]');

    let imageUrl = null;

    if (variant) {
      if (variant.featured_image?.src) {
        imageUrl = variant.featured_image.src;
      } else if (typeof variant.featured_image === 'string' && variant.featured_image) {
        imageUrl = variant.featured_image;
      } else if (variant.image?.src) {
        imageUrl = variant.image.src;
      } else if (typeof variant.image === 'string' && variant.image) {
        imageUrl = variant.image;
      } else if (variant.image_id && this.product.images) {
        const matchingImage = this.product.images.find(img => img.id === variant.image_id);
        if (matchingImage) {
          imageUrl = matchingImage.src || matchingImage;
        }
      }
    }

    // Fall back to product featured image
    if (!imageUrl && this.product.featured_image) {
      if (typeof this.product.featured_image === 'string') {
        imageUrl = this.product.featured_image;
      } else if (this.product.featured_image.src) {
        imageUrl = this.product.featured_image.src;
      }
    }

    // Fall back to first product image
    if (!imageUrl && this.product.images && this.product.images.length > 0) {
      const firstImage = this.product.images[0];
      imageUrl = firstImage.src || firstImage;
    }

    if (imageElement && imageUrl) {
      const fullImageUrl = imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl;
      imageElement.src = fullImageUrl;
      imageElement.style.display = 'block';
      imageElement.style.opacity = '1';

      if (imageContainer) {
        imageContainer.classList.add('image-loaded');
      }
    }
  }

  showStep(stepNumber) {
    for (let i = 1; i <= stepNumber; i++) {
      const step = this.querySelector(`[js-step="${i}"]`);
      if (step) {
        step.style.display = 'block';
        if (i === stepNumber) {
          step.classList.add('active');
        }
      }
    }
  }

  setupDatePicker() {
    console.log('→ Setting up date picker');

    const dateInput = this.querySelector('[js-event-date]');
    if (!dateInput) {
      console.log('ℹ No date input found');
      return;
    }

    // Set minimum date to today
    const today = new Date();
    const minDate = today.toISOString().split('T')[0];
    dateInput.setAttribute('min', minDate);

    dateInput.addEventListener('change', () => {
      this.eventDate = dateInput.value;
      console.log('✓ Event date selected:', this.eventDate);
      this.updateSummary();
    });

    console.log('✓ Date picker set up complete');
  }

  getSelections() {
    const selections = {
      variant: this.selectedVariant,
      eventDate: this.eventDate
    };

    // Add selected options with their labels
    Object.keys(this.selectedOptions).forEach((key, index) => {
      const optionName = this.product.options[index];
      selections[`option${index + 1}`] = this.selectedOptions[key];
      selections[`option${index + 1}_name`] = optionName;
    });

    return selections;
  }

  updateSummary() {
    const selections = this.getSelections();

    // Update variant option values in summary
    for (let i = 1; i <= this.product.options.length; i++) {
      const summaryElement = this.querySelector(`[js-summary-option-${i}]`);
      if (summaryElement) {
        summaryElement.textContent = selections[`option${i}`] || '-';
      }
    }

    // Update date summary
    const dateSummary = this.querySelector('[js-date-summary]');
    const dateValueElement = this.querySelector('[js-summary-date]');
    if (dateSummary && dateValueElement) {
      if (selections.eventDate) {
        dateSummary.style.display = '';
        // Format date nicely
        const date = new Date(selections.eventDate + 'T00:00:00');
        const formattedDate = date.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        dateValueElement.textContent = formattedDate;
      } else {
        dateSummary.style.display = 'none';
      }
    }

    // Calculate prices
    let basePrice = 0;

    if (this.selectedVariant) {
      basePrice = this.selectedVariant.price / 100;
    }

    // Update price display
    const basePriceElement = this.querySelector('[js-base-price]');
    if (basePriceElement) {
      basePriceElement.textContent = this.formatPrice(basePrice);
    }

    // Calculate and display total
    const totalPriceElement = this.querySelector('[js-total-price]');
    if (totalPriceElement) {
      totalPriceElement.textContent = this.formatPrice(basePrice);
    }

    // Show summary section
    const summarySection = this.querySelector('[js-summary]');
    if (summarySection && this.selectedVariant) {
      summarySection.style.display = 'block';
    }
  }

  formatPrice(price) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  }

  setupAddToCart() {
    console.log('→ Setting up add to cart');

    const addToCartBtn = this.querySelector('[js-add-to-cart]');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', () => {
        this.handleAddToCart();
      });
    }

    console.log('✓ Add to cart set up complete');
  }

  async handleAddToCart() {
    const addToCartBtn = this.querySelector('[js-add-to-cart]');
    const selections = this.getSelections();

    console.log('→ Adding to cart with selections:', selections);

    // Validate selections
    if (!this.selectedVariant) {
      alert('Please complete all selections before adding to cart');
      return;
    }

    if (!this.selectedVariant.available) {
      alert('This variant is currently out of stock');
      return;
    }

    // Get quantity
    const quantityInput = this.querySelector('[name="quantity"]');
    const quantity = parseInt(quantityInput?.value || 1);

    // Build line item properties for the main product
    const properties = {};

    // Add variant options as properties
    for (let i = 1; i <= this.product.options.length; i++) {
      if (selections[`option${i}_name`] && selections[`option${i}`]) {
        properties[selections[`option${i}_name`]] = selections[`option${i}`];
      }
    }

    // Add event date as property
    if (selections.eventDate) {
      const date = new Date(selections.eventDate + 'T00:00:00');
      const formattedDate = date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      properties['Event Date'] = formattedDate;
    }

    // Disable button
    addToCartBtn.disabled = true;
    addToCartBtn.textContent = 'Adding...';

    try {
      // Add the main product
      console.log('→ Adding product to cart');
      const mainResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: this.selectedVariant.id,
            quantity: quantity,
            properties: properties
          }]
        })
      });

      if (!mainResponse.ok) {
        throw new Error(`HTTP error! status: ${mainResponse.status}`);
      }

      const mainResult = await mainResponse.json();
      console.log('✓ Product added:', mainResult);

      // Success feedback
      addToCartBtn.textContent = 'Added to Cart!';
      setTimeout(() => {
        addToCartBtn.textContent = 'Add to Cart';
        addToCartBtn.disabled = false;
      }, 2000);

      // Trigger cart drawer to open
      this.openCartDrawer();

    } catch (error) {
      console.error('✗ Failed to add to cart:', error);
      alert('Failed to add to cart. Please try again.');
      addToCartBtn.textContent = 'Add to Cart';
      addToCartBtn.disabled = false;
    }
  }

  openCartDrawer() {
    console.log('→ Opening cart drawer');

    // Dispatch custom events for cart update
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('theme:cart:change'));

    // Try common theme-specific cart drawer triggers
    if (typeof window.theme !== 'undefined' && window.theme.openCartDrawer) {
      window.theme.openCartDrawer();
      console.log('✓ Called window.theme.openCartDrawer()');
    } else if (typeof window.Shopify !== 'undefined' && window.Shopify.theme && window.Shopify.theme.cart) {
      window.Shopify.theme.cart.open();
      console.log('✓ Called window.Shopify.theme.cart.open()');
    }

    // Try to find and click cart drawer toggle
    const selectors = [
      '[data-cart-drawer-toggle]',
      '.cart-drawer-toggle',
      '[data-action="toggle-cart"]',
      '[aria-controls*="cart"]',
      '.header__icon--cart',
      '#cart-icon-bubble',
      'a[href="/cart"]',
      '.cart-count-bubble',
      '[data-cart-icon]',
      'cart-notification-button',
      'cart-drawer-toggle',
      '.cart-link',
      '[data-open-cart]'
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        console.log(`✓ Found cart element: ${selector}`);
        element.click();
        console.log('✓ Clicked cart drawer toggle');
        break;
      }
    }

    // Try to open cart drawer by setting attribute
    const cartDrawer = document.querySelector('cart-drawer, [id*="cart-drawer"], .cart-drawer');
    if (cartDrawer) {
      cartDrawer.setAttribute('open', '');
      cartDrawer.classList.add('active', 'open');
      console.log('✓ Set cart drawer open attribute');
    }

    console.log('✓ Cart drawer triggers dispatched');
  }
}

// Register the custom element
customElements.define('air-dried-frame', AirDriedFrame);
